// Single source of truth for SEO landing page URL paths across locales.
//
// • English keeps its existing un-prefixed paths (already indexed).
// • Spanish and French Canadian get translated slugs under /es/ and
//   /fr-ca/ — best practice for keyword-rich URLs in those markets.
// • Punjabi keeps the English slug under /pa/ — standard practice for
//   non-Latin-script locales (Google's hreflang docs explicitly allow
//   this and most large multilingual sites do it for Indic scripts).
//
// The map is consumed in three places:
//   1. App.tsx — generates a wouter <Route> per (slug, locale) pair.
//   2. SeoPageLayout — emits <link rel="alternate" hreflang="…"> for
//      every other locale variant of the current slug, plus x-default.
//   3. public/sitemap.xml — must be kept in sync (manual XML; there's
//      no build step that emits it from this file).

import type { SeoLocale } from "./types";

export type SeoSlug =
  | "how-it-works"
  | "family-scavenger-hunt"
  | "christmas-eve-family-activity"
  | "indoor-christmas-activity"
  | "santa-clues-for-kids"
  | "christmas-mission-app"
  | "family-christmas-app"
  | "christmas-photo-challenge"
  | "elf-on-the-shelf-alternative"
  | "christmas-activities-for-grandparents-and-kids"
  | "ai-summary";

export const SEO_PAGE_PATHS: Record<SeoSlug, Record<SeoLocale, string>> = {
  "how-it-works": {
    "en": "/how-it-works",
    "es": "/es/como-funciona",
    "fr-CA": "/fr-ca/comment-ca-marche",
    "pa-Guru": "/pa/how-it-works",
  },
  "family-scavenger-hunt": {
    "en": "/family-scavenger-hunt",
    "es": "/es/busqueda-del-tesoro-familiar-de-navidad",
    "fr-CA": "/fr-ca/chasse-au-tresor-familiale-de-noel",
    "pa-Guru": "/pa/family-scavenger-hunt",
  },
  "christmas-eve-family-activity": {
    "en": "/christmas-eve-family-activity",
    "es": "/es/actividad-familiar-en-nochebuena",
    "fr-CA": "/fr-ca/activite-familiale-veille-de-noel",
    "pa-Guru": "/pa/christmas-eve-family-activity",
  },
  "indoor-christmas-activity": {
    "en": "/indoor-christmas-activity",
    "es": "/es/actividad-navidena-en-casa",
    "fr-CA": "/fr-ca/activite-de-noel-a-la-maison",
    "pa-Guru": "/pa/indoor-christmas-activity",
  },
  "santa-clues-for-kids": {
    "en": "/santa-clues-for-kids",
    "es": "/es/pistas-de-papa-noel-para-ninos",
    "fr-CA": "/fr-ca/indices-du-pere-noel-pour-enfants",
    "pa-Guru": "/pa/santa-clues-for-kids",
  },
  "christmas-mission-app": {
    "en": "/christmas-mission-app",
    "es": "/es/aplicacion-de-mision-navidena",
    "fr-CA": "/fr-ca/application-mission-de-noel",
    "pa-Guru": "/pa/christmas-mission-app",
  },
  "family-christmas-app": {
    "en": "/family-christmas-app",
    "es": "/es/aplicacion-navidena-familiar",
    "fr-CA": "/fr-ca/application-de-noel-familiale",
    "pa-Guru": "/pa/family-christmas-app",
  },
  "christmas-photo-challenge": {
    "en": "/christmas-photo-challenge",
    "es": "/es/desafio-de-fotos-navidenas",
    "fr-CA": "/fr-ca/defi-photo-de-noel",
    "pa-Guru": "/pa/christmas-photo-challenge",
  },
  "elf-on-the-shelf-alternative": {
    "en": "/elf-on-the-shelf-alternative",
    "es": "/es/alternativa-al-elfo-en-el-estante",
    "fr-CA": "/fr-ca/alternative-au-lutin-farceur",
    "pa-Guru": "/pa/elf-on-the-shelf-alternative",
  },
  "christmas-activities-for-grandparents-and-kids": {
    "en": "/christmas-activities-for-grandparents-and-kids",
    "es": "/es/actividades-navidenas-para-abuelos-y-ninos",
    "fr-CA": "/fr-ca/activites-de-noel-grands-parents-et-enfants",
    "pa-Guru": "/pa/christmas-activities-for-grandparents-and-kids",
  },
  "ai-summary": {
    "en": "/ai-summary",
    "es": "/es/resumen-ia",
    "fr-CA": "/fr-ca/resume-ia",
    "pa-Guru": "/pa/ai-summary",
  },
};

export const SEO_SLUGS: SeoSlug[] = Object.keys(SEO_PAGE_PATHS) as SeoSlug[];

// Per-(slug, locale) primary SEO keyword the page's <title> must
// contain in-locale. Encoded here so it can't drift away from the
// title on a future copy edit and silently re-rank the page for the
// wrong query.
//
// Enforced by `scripts/src/check-seo-meta.ts`, which fails
// `pnpm typecheck` if any locale's <title> drops its keyword
// (case-insensitive substring match).
//
// Picking the keyword:
//   • Use the topical phrase a parent in that locale would actually
//     search for, not the brand ("JINGLE") and not a generic word
//     that would match unrelated pages too.
//   • For pa-Guru, follow the in-app code-switching pattern: English
//     nouns stay in Latin script, Gurmukhi connectors join them
//     (e.g. "Grandparents ਅਤੇ Kids"). Match what the title actually
//     says — don't force a Gurmukhi-only phrase that the title
//     doesn't carry.
//   • The phrase must be a literal substring of the current title
//     (case-insensitive). If a future redesign genuinely changes the
//     primary keyword, update this map *and* the title together.
export const SEO_PAGE_PRIMARY_KEYWORDS: Record<
  SeoSlug,
  Record<SeoLocale, string>
> = {
  "how-it-works": {
    "en": "how JINGLE works",
    "es": "cómo funciona",
    "fr-CA": "comment fonctionne",
    "pa-Guru": "ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ",
  },
  "family-scavenger-hunt": {
    "en": "family Christmas scavenger hunt",
    "es": "búsqueda del tesoro familiar",
    "fr-CA": "chasse au trésor familiale",
    "pa-Guru": "Family Christmas Scavenger Hunt",
  },
  "christmas-eve-family-activity": {
    "en": "Christmas Eve Family Activity",
    "es": "actividad familiar para Nochebuena",
    "fr-CA": "activité familiale pour la veille de Noël",
    "pa-Guru": "Christmas Eve Family Activity",
  },
  "indoor-christmas-activity": {
    "en": "indoor Christmas activity",
    "es": "actividad navideña en casa",
    "fr-CA": "activité de Noël à la maison",
    "pa-Guru": "Indoor Christmas Activity",
  },
  "santa-clues-for-kids": {
    "en": "Santa clues for kids",
    "es": "pistas de Papá Noel",
    "fr-CA": "indices du Père Noël",
    "pa-Guru": "Santa Clues",
  },
  "christmas-mission-app": {
    "en": "Family Christmas Mission App",
    "es": "Aplicación familiar de Misión Navideña",
    "fr-CA": "Application familiale de Mission de Noël",
    "pa-Guru": "Family Christmas Mission App",
  },
  "family-christmas-app": {
    "en": "Family Christmas App",
    "es": "aplicación navideña familiar",
    "fr-CA": "application de Noël familiale",
    "pa-Guru": "Family Christmas App",
  },
  "christmas-photo-challenge": {
    "en": "Christmas Photo Challenge",
    "es": "desafío de fotos navideñas",
    "fr-CA": "défi photo de Noël",
    "pa-Guru": "Christmas Photo Challenge",
  },
  "elf-on-the-shelf-alternative": {
    "en": "Elf on the Shelf Alternative",
    "es": "Alternativa al Elfo en el Estante",
    "fr-CA": "Alternative au Lutin Farceur",
    "pa-Guru": "Elf on the Shelf Alternative",
  },
  "christmas-activities-for-grandparents-and-kids": {
    "en": "Christmas Activities for Grandparents and Kids",
    "es": "actividades navideñas para abuelos y niños",
    "fr-CA": "activités de Noël pour grands-parents et enfants",
    "pa-Guru": "Grandparents ਅਤੇ Kids",
  },
  "ai-summary": {
    "en": "AI Summary",
    "es": "Resumen IA",
    "fr-CA": "Résumé IA",
    "pa-Guru": "AI Summary",
  },
};
